<script setup lang="ts">
import { cva } from "class-variance-authority";
import { computed } from "vue";

import { useLayout } from "@/layout/common/composables/layout";

const props = defineProps({
  class: {
    type: String,
    default: "",
  },
});

const SIDEBAR_WIDTH = {
  static: "16rem",
  slim: "5rem",
  compact: "3.25rem",
  horizontal: "100%",
  overlay: "18rem",
  drawer: "16rem",
  reveal: "16rem",
};

const { layoutConfig, calculatedMenuMode, onClose, setMenuState } = useLayout();

const handleBackdropClick = () => {
  if (calculatedMenuMode.value === "overlay") {
    onClose();
  }
};

const handleMouseEnter = () => {
  if (
    (calculatedMenuMode.value === "drawer" ||
      calculatedMenuMode.value === "reveal") &&
    !layoutConfig.anchored
  ) {
    setMenuState("expanded");
  }
};

const handleMouseLeave = () => {
  if (
    (calculatedMenuMode.value === "drawer" ||
      calculatedMenuMode.value === "reveal") &&
    !layoutConfig.anchored
  ) {
    setMenuState("collapsed");
  }
};

const sidebarVariants = cva(
  "group/sidebar-wrapper !duration-200 ease-linear -my-2 flex overflow-y-auto",
  {
    variants: {
      variant: {
        static:
          "relative h-full fixed inset-y-0 flex-col w-[--sidebar-width] data-[state=collapsed]:-left-[calc(var(--sidebar-width)*1.02)] data-[state=expanded]:left-0 data-[state=collapsed]:pointer-events-none transition-[transform,max-width,left]",
        slim: "relative h-full flex-col w-[--sidebar-width] ",
        overlay:
          "fixed h-full flex-col pt-4 top-0 left-0 z-[1000] dark:border-r w-full min-[400px]:w-[--sidebar-width] h-full bg-surface-0 dark:bg-surface-950 shadow-lg data-[state=collapsed]:pointer-events-none data-[state=expanded]:pointer-events-auto data-[state=collapsed]:-translate-x-full data-[state=expanded]:translate-x-0 transition-[transform,max-width]",
        compact: "relative h-full flex-col w-[--sidebar-width] ",
        horizontal: "relative w-full flex-row w-[--sidebar-width] w-full",
        drawer:
          "relative h-full flex-col data-[state=collapsed]:w-[3.25rem] data-[state=expanded]:w-[--sidebar-width] !transition-[width]",
        reveal:
          "!items-end relative h-full flex-col data-[state=collapsed]:w-[3.25rem] data-[state=expanded]:w-[--sidebar-width] !transition-[width]",
      },
      state: {
        expanded: "",
        collapsed: "",
      },
    },
    defaultVariants: {
      variant: "static",
      state: "expanded",
    },
  },
);

const sidebarWrapperVariants = cva(
  "relative z-[1000] transition-[transform,max-width,left] !duration-200 ease-linear ",
  {
    variants: {
      variant: {
        static:
          "data-[state=collapsed]:w-0 data-[state=expanded]:w-[--sidebar-width] transition-[width] duration-200 ease-linear overflow-visible",
        horizontal: "pb-2 w-full mx-auto max-w-[1720px] ",
        drawer: "overflow-hidden",
      },
    },
    defaultVariants: {
      variant: "static",
    },
  },
);
type MenuMode = "static" | "overlay" | "drawer" | "reveal" | "slim" | "compact" | "horizontal";
type MenuState = "expanded" | "collapsed";

const sidebarClass = computed(() => {
  return sidebarVariants({
    variant: calculatedMenuMode.value as MenuMode,
    state: layoutConfig.menuState as MenuState,
    class: props.class,
  });
});

const sidebarWidth = computed(() => {
  const mode = calculatedMenuMode.value as keyof typeof SIDEBAR_WIDTH;

  return SIDEBAR_WIDTH[mode];
});

type WrapperMenuMode = "static" | "drawer" | "horizontal";

const sidebarWrapperClass = computed(() => {
  return sidebarWrapperVariants({
    variant: calculatedMenuMode.value as WrapperMenuMode,
  });
});
</script>

<template>
  <div
    :data-state="layoutConfig.menuState"
    :data-variant="calculatedMenuMode"
    class="fixed inset-0 z-[999] bg-black/25 dark:bg-black/75 data-[state=collapsed]:pointer-events-none data-[state=collapsed]:opacity-0 data-[state=expanded]:pointer-events-auto data-[state=expanded]:opacity-100 transition-all duration-300"
    v-if="calculatedMenuMode === 'overlay'"
    @click="handleBackdropClick"
  />

  <div
    :class="sidebarWrapperClass"
    :style="{
      '--sidebar-width': sidebarWidth,
    }"
    :data-state="layoutConfig.menuState"
    :data-variant="calculatedMenuMode"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <div
      :class="sidebarClass"
      :style="{
        '--sidebar-width': sidebarWidth,
      }"
      :data-state="layoutConfig.menuState"
      :data-variant="calculatedMenuMode"
    >
      <slot />
    </div>
  </div>
</template>
